
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/AuthProvider";
import { Json } from "@/integrations/supabase/types";

export interface Translation {
  language: string;
  nativeScript: string;
  transliteration: string;
  definition: string;
  note: string;
}

export interface KeyUsage {
  reference: string;
  verse: string;
  briefNote: string;
}

export interface EtymologyData {
  term: string;
  primaryOriginLanguage: string;
  primaryWordOrPhrase: string;
  translations: Translation[];
  keyUsages: KeyUsage[];
  relatedTerms: string[];
  theologicalInsight: string;
}

export const useEtymology = () => {
  const { user } = useAuth();
  const [term, setTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [etymology, setEtymology] = useState<EtymologyData | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!term.trim()) {
      toast({
        title: "Please enter a word or phrase",
        description: "Enter a biblical word or phrase to explore its etymology.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setEtymology(null);

    try {
      const { data, error } = await supabase.functions.invoke("etymology", {
        body: {
          term: term.trim(),
        },
      });

      if (error) throw error;

      if (data) {
        setEtymology(data);
      } else {
        throw new Error("No etymology data received");
      }
    } catch (error) {
      console.error("Error fetching etymology:", error);
      toast({
        title: "Error fetching etymology",
        description: "Please try again later or try a different term.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveEtymology = async () => {
    if (!etymology || !user) {
      toast({
        title: "Cannot save etymology",
        description: user ? "No etymology data to save" : "Please sign in to save etymology",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      
      // Fix: Convert the object to a JSON-compatible format by explicitly casting as unknown first
      const contentObj = {
        term: etymology.term,
        etymology: {
          term: etymology.term,
          primaryOriginLanguage: etymology.primaryOriginLanguage,
          primaryWordOrPhrase: etymology.primaryWordOrPhrase,
          translations: etymology.translations,
          keyUsages: etymology.keyUsages,
          relatedTerms: etymology.relatedTerms,
          theologicalInsight: etymology.theologicalInsight
        },
        createdAt: new Date().toISOString()
      } as unknown as Json;

      const { error: insertError } = await supabase
        .from('saved_responses')
        .insert({
          tool_type: 'etymology',
          title: `Etymology: ${etymology.term}`,
          content: contentObj,
          user_id: user.id
        });

      if (insertError) throw insertError;

      toast({
        title: "Etymology Saved",
        description: "Your etymology search has been saved to your dashboard.",
      });
    } catch (error) {
      console.error('Error saving etymology:', error);
      toast({
        title: "Error",
        description: "Failed to save etymology. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    term,
    setTerm,
    isLoading,
    isSaving,
    etymology,
    handleSubmit,
    saveEtymology,
  };
};
